/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionButtonType, Switch } from "@octopusdeploy/design-system-components";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import { useInlineStatusMutation } from "@octopusdeploy/octopus-react-client";
import { type EnvironmentResource, type FeatureToggleEnvironmentResource, type FeatureToggleResource, type ProjectResource, type Repository, type TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useMemo, useState } from "react";
import EditToggleDialog from "~/areas/projects/components/FeatureToggles/EditFeatureToggleEnvironmentDialog";
import ToggleScope from "~/areas/projects/components/FeatureToggles/FeatureToggleEnvironment";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { repository } from "~/clientInstance";
import DialogOpener from "~/components/Dialog/DialogOpener";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import Markdown from "~/components/Markdown/Markdown";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { RemoveItemsList } from "~/components/RemoveItemsList/RemoveItemsList";
import { Form, useForm } from "~/components/form/Form/Form";
import { ExpandableFormSection, FormSection, MarkdownEditor, required, Summary } from "~/components/form/index";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
export interface FeatureTogglePageData {
    project: ProjectResource;
    toggle: FeatureToggleResource;
    environments: EnvironmentResource[];
    tenants: TenantResource[];
}
export async function featureTogglePageLoader(repository: Repository, slug: string, projectContext: Promise<ProjectContextProps>): Promise<FeatureTogglePageData> {
    const { state: { model: project }, } = await projectContext;
    const environments = repository.Projects.getAvailableEnvironmentsForProject(project.Id);
    const tenants = repository.Tenants.all({ projectId: project.Id });
    const toggle = repository.FeatureToggles.get(project, slug);
    return {
        environments: (await environments).Environments,
        tenants: await tenants,
        toggle: await toggle,
        project,
    };
}
export interface FeatureTogglePageProps {
    data: FeatureTogglePageData;
}
interface EditState {
    environmentEdit: FeatureToggleEnvironmentResource;
    environmentEditIdx: number;
}
class ToggleEnvironmentList extends RemoveItemsList<FeatureToggleEnvironmentResource> {
}
export function FeatureTogglePage({ data }: FeatureTogglePageProps) {
    const { tenants, environments, project, toggle: initialToggle } = data;
    const { navigate } = useSpaceAwareNavigation();
    const { model: toggle, setModel: setToggle, formProps, createSaveAction, } = useForm({
        formName: "Feature toggle",
        initialModel: initialToggle,
        onSubmit: async (repository, model) => {
            return await repository.FeatureToggles.modify(data.project, model);
        },
        afterSubmit: async (_, updatedToggle) => {
            if (toggle.Slug !== updatedToggle.Slug) {
                navigate(links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: updatedToggle.Slug }));
            }
        },
    });
    const overflowMenu = useMemo(() => {
        async function deleteToggle(toDelete: FeatureToggleResource): Promise<boolean> {
            if (!toDelete)
                return false;
            await repository.FeatureToggles.delete(project, toDelete.Id);
            navigate(links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }));
            return true;
        }
        const items = [OverflowMenuItems.deleteItemDefault("feature toggle", () => deleteToggle(toggle)), OverflowMenuItems.dialogItem("Clone", <CloneToggleDialog project={project} toggle={toggle}/>)];
        return OverflowMenuConverterVNext.convertAll(items);
    }, [project, toggle, navigate]);
    return (<PageContent header={{
            title: toggle.Name,
            overflowActions: overflowMenu.menuItems,
            breadcrumbs: [{ label: "Feature Toggles", pageUrl: links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }) }],
            primaryAction: createSaveAction(),
        }}>
            {overflowMenu.dialogs}
            <Form {...formProps}>
                <EditFeatureToggle environments={environments} tenants={tenants} model={toggle} setModel={setToggle}/>
            </Form>
        </PageContent>);
}
interface EditFeatureToggleProps {
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    model: FeatureToggleResource;
    setModel: (value: React.SetStateAction<FeatureToggleResource>) => void;
}
function EditFeatureToggle({ environments, tenants, model, setModel }: EditFeatureToggleProps) {
    const [editState, setEditState] = useState<EditState | null>(null);
    const [originalSlug] = useState(model.Slug);
    function nameSummary() {
        if (!model.Name)
            return Summary.placeholder("Please enter a name for the feature");
        return Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>);
    }
    function noteSummary() {
        if (!model.Description)
            return Summary.placeholder("Enter markdown notes");
        return Summary.summary(<Markdown markup={model.Description}/>);
    }
    function defaultValueSummary() {
        return Summary.summary(model.DefaultIsEnabled ? "On" : "Off");
    }
    function environmentRow(toggle: FeatureToggleEnvironmentResource, idx: number) {
        const environment = environments.find((e) => e.Id === toggle.DeploymentEnvironmentId);
        if (!environment) {
            throw new Error("Could not find environment");
        }
        const rowTenants = tenants.filter((t) => toggle.TenantIds.indexOf(t.Id) !== -1 || toggle.ExcludedTenantIds.indexOf(t.Id) !== -1);
        const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation();
        return (<div>
                <span onClick={handleClick}>
                    <Switch value={toggle.IsEnabled} onChange={(value) => setToggleEnvironment(value, idx)}/>
                </span>
                <ToggleScope featureToggleEnvironment={toggle} environmentName={environment.Name} tenants={rowTenants}/>
                {toggle.MinimumVersion && (<>
                        <code>&gt;= {toggle.MinimumVersion}</code>
                    </>)}
            </div>);
    }
    function setToggleEnvironment(value: boolean, idx: number) {
        const toggleEnvironments = [...model.Environments];
        toggleEnvironments[idx] = { ...toggleEnvironments[idx], IsEnabled: value };
        setModel({ ...model, Environments: toggleEnvironments });
    }
    async function addToggleEnvironment(environment: FeatureToggleEnvironmentResource) {
        setModel({ ...model, Environments: [...model.Environments, environment] });
        return true;
    }
    async function saveToggleEnvironment(environment: FeatureToggleEnvironmentResource) {
        const toggles = [...model.Environments];
        if (editState && editState.environmentEditIdx !== null) {
            toggles[editState.environmentEditIdx] = environment;
        }
        else {
            toggles.push(environment);
        }
        setModel({ ...model, Environments: toggles });
        return true;
    }
    async function removeToggleEnvironment(toggle: FeatureToggleEnvironmentResource) {
        setModel({
            ...model,
            Environments: model.Environments.filter((t) => t !== toggle),
        });
    }
    return (<div>
            {editState && (<DialogOpener open={true} onClose={() => setEditState(null)} wideDialog={true}>
                    <EditToggleDialog title="Edit Environment" onSave={saveToggleEnvironment} environments={environments} tenants={tenants} featureToggleEnvironment={editState.environmentEdit}/>
                </DialogOpener>)}

            <ExpandableFormSection errorKey="Name" title="Name" summary={nameSummary()}>
                <Text value={model.Name} label={"Feature Name"} validate={required("Please provide a name for the feature")} onChange={(value) => {
            setModel({ ...model, Name: value });
        }}/>
                <SlugEditor label="Slug" value={model.Slug} originalSlug={originalSlug} readOnly={false} onChange={(value) => {
            setModel({ ...model, Slug: value });
        }} additionalWarningMessage={"Modifying a slug may impact feature toggle evaluations for software referencing the slug. Any references to the old slug will have to be manually updated, and careful consideration should be given to managing the changeover from the old slug to the new slug."} validate={required("Please enter a feature toggle slug")}/>
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="Description" title="Notes" summary={noteSummary()}>
                <MarkdownEditor label="Notes" value={model.Description} onChange={(value) => {
            setModel({ ...model, Description: value });
        }}/>
            </ExpandableFormSection>
            <ExpandableFormSection title="Default Value" errorKey="DefaultValue" summary={defaultValueSummary()} help={"The default value will be returned for any environments not configured below"}>
                <Switch value={model.DefaultIsEnabled} onChange={(value) => {
            setModel({ ...model, DefaultIsEnabled: value });
        }}/>
            </ExpandableFormSection>
            <FormSection title="Environments" includeBorder={true}>
                <OpenDialogButton label="Add Environment" type={ActionButtonType.Secondary} dialogWidth={"1000px"}>
                    <EditToggleDialog title="Add Environment" onSave={addToggleEnvironment} environments={environments} tenants={tenants}/>
                </OpenDialogButton>
                <ToggleEnvironmentList data={model.Environments} onRow={(toggle, idx) => environmentRow(toggle, idx)} onRowTouch={(t) => setEditState({ environmentEdit: t, environmentEditIdx: model.Environments.indexOf(t) })} onRemoveRow={removeToggleEnvironment}/>
            </FormSection>
        </div>);
}
interface CloneToggleDialogProps {
    project: ProjectResource;
    toggle: FeatureToggleResource;
}
function CloneToggleDialog({ project, toggle }: CloneToggleDialogProps) {
    const { navigate } = useSpaceAwareNavigation();
    const [name, setName] = useState(toggle.Name);
    const { error, execute: clone, isExecuting: busy, } = useInlineStatusMutation({
        name: "Clone Feature Toggle",
        action: async (repository) => {
            return await repository.FeatureToggles.clone(project, toggle.Id, name);
        },
        afterComplete: async (_, clonedToggle) => {
            navigate(links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: clonedToggle.Slug }));
        },
    });
    const errors = error ? [error] : undefined;
    return (<SaveDialogLayout title="Clone Feature Toggle" cancelButtonLabel="Cancel" saveButtonLabel="Clone" errors={errors} busy={busy} onSaveClick={async () => {
            await clone();
            return true;
        }}>
            <Text value={name} label="Name" onChange={(value) => setName(value)} autoFocus/>
        </SaveDialogLayout>);
}
